// src/components/Signature/index.tsx
import React5, { Component } from "react";
import { Button as Button4, Dialog as Dialog2, DialogContent as DialogContent2, DialogTitle as DialogTitle2, IconButton } from "@mui/material";
import ReactSignatureCanvas from "react-signature-canvas";

// src/components/Signature/fontFamilies.ts
var fontFamilies = {
  Pacifico: {
    styles: { fontFamily: "'Pacifico', cursive" },
    import: "Pacifico:400,500,600,700"
  },
  "Shadows Into Light": {
    styles: { fontFamily: "'Shadows Into Light', cursive" },
    import: "Shadows+Into+Light:400,500,600,700"
  },
  "Amatic SC": {
    styles: { fontFamily: "'Amatic SC', cursive" },
    import: "Amatic+SC:400,500,600,700"
  },
  "Happy Monkey": {
    styles: { fontFamily: "'Happy Monkey', cursive" },
    import: "Happy+Monkey:400,500,600,700"
  },
  "Dancing Script": {
    styles: { fontFamily: "'Dancing Script', cursive" },
    import: "Dancing+Script:400,500,600,700"
  },
  Dosis: {
    styles: { fontFamily: "'Dosis', sans-serif" },
    import: "Dosis:400,500,600,700"
  },
  Cinzel: {
    styles: { fontFamily: "'Cinzel', serif" },
    import: "Cinzel:400,500,600,700"
  },
  Lobster: {
    styles: { fontFamily: "'Lobster', cursive" },
    import: "Lobster:400,500,600,700"
  },
  Amiri: {
    styles: { fontFamily: "'Amiri', serif" },
    import: "Amiri:400,500,600,700"
  }
};
var fontFamilies_default = fontFamilies;

// src/components/Signature/DigitalSignaturePicker.tsx
import { Button } from "@mui/material";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
function DigitalSignaturePicker({ user, onClickGoback, value, onSelectFont }) {
  const fonts = Object.keys(fontFamilies_default);
  const [selected, setSelected] = useState(value);
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx("div", {
        className: "sign-text-md",
        children: "Select your digital signature"
      }),
      /* @__PURE__ */ jsx("div", {
        className: "sign-grid sign-pt-2 sign-grid-cols-3 sign-min-w-[500px] sign-gap-4",
        children: fonts.map((font) => /* @__PURE__ */ jsx("div", {
          className: twMerge(`
            sign-col-span-1 sign-cursor-pointer
            sign-outline-neutral-200 sign-flex sign-items-center sign-justify-center sign-min-h-[50px] sign-outline sign-outline-1 sign-rounded-md
            sign-shadow-sm sign-text-center sign-py-4
          `, font === selected && "!sign-outline-green-600"),
          onClick: () => setSelected(font),
          children: /* @__PURE__ */ jsx("span", {
            style: { ...fontFamilies_default[font].styles, fontSize: "16px" },
            children: `${user == null ? void 0 : user.name}`
          })
        }, font))
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "sign-flex sign-gap-4 sign-pt-6",
        children: [
          /* @__PURE__ */ jsx(Button, {
            onClick: onClickGoback,
            children: "Go back"
          }),
          /* @__PURE__ */ jsx(Button, {
            variant: "contained",
            disabled: !selected,
            onClick: () => selected && onSelectFont(selected),
            children: "Ok"
          })
        ]
      })
    ]
  });
}
var DigitalSignaturePicker_default = DigitalSignaturePicker;

// src/icons/Close.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var CloseIcon = function CloseIcon2({
  className,
  width,
  height
}) {
  return /* @__PURE__ */ jsx2("svg", {
    className,
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24",
    strokeWidth: 1.5,
    stroke: "currentColor",
    width,
    height,
    children: /* @__PURE__ */ jsx2("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    })
  });
};
CloseIcon.defaultProps = {
  className: "",
  width: 24,
  height: 24
};
var Close_default = CloseIcon;

// src/utils/index.ts
var parseUserAgent = (userAgent) => {
  const matches = userAgent.match(/\(([^)]+)\)/);
  return matches ? matches[1] : "";
};
function calculateFontSize(width, height, stringLength) {
  if (stringLength === 0)
    return 0;
  const fontSize = Math.sqrt(width * height / stringLength);
  return Math.min(fontSize, height);
}
var toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
});

// src/components/Signature/DigitalSignatureValue.tsx
import { useEffect, useRef, useState as useState2 } from "react";
import { Fragment as Fragment2, jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function DigitalSignatureValue({
  value,
  styles,
  readonly,
  preview,
  onClick
}) {
  var _a, _b, _c;
  const parentRef = useRef(null);
  const [dimensions, setDimensions] = useState2({
    width: 100,
    height: 30
  });
  useEffect(() => {
    var _a2;
    if (parentRef.current) {
      const domRect = (_a2 = parentRef == null ? void 0 : parentRef.current) == null ? void 0 : _a2.getBoundingClientRect();
      setDimensions({
        width: domRect.width,
        height: domRect.height
      });
    }
  }, [parentRef.current]);
  return /* @__PURE__ */ jsxs2(Fragment2, {
    children: [
      (value == null ? void 0 : value.type) === "digital" /* digital */ && /* @__PURE__ */ jsx3("div", {
        ref: parentRef,
        className: readonly ? "sign-cursor-not-allowed" : "sign-cursor-pointer",
        style: {
          width: "100%",
          height: "100%",
          lineHeight: preview ? "inherit" : `${calculateFontSize(dimensions.width, dimensions.height, (_a = value.data) == null ? void 0 : _a.name.length)}px`,
          textAlign: "center",
          ...styles == null ? void 0 : styles.root
        },
        onClick,
        children: /* @__PURE__ */ jsx3("span", {
          style: {
            fontSize: preview ? "35px" : calculateFontSize(dimensions.width, dimensions.height, (_b = value.data) == null ? void 0 : _b.name.length),
            ...fontFamilies_default[value.data.fontFamily].styles,
            ...styles == null ? void 0 : styles.digitalSignature
          },
          children: `${(_c = value.data) == null ? void 0 : _c.name}`
        })
      }),
      (value == null ? void 0 : value.type) === "draw" /* draw */ && /* @__PURE__ */ jsx3("div", {
        ref: parentRef,
        className: readonly ? "sign-cursor-not-allowed" : "sign-cursor-pointer",
        style: { width: "100%", height: "100%", textAlign: "center", ...styles == null ? void 0 : styles.root },
        onClick,
        children: /* @__PURE__ */ jsx3("img", {
          src: value.data.image,
          style: { height: "100%", maxWidth: "100%" }
        })
      })
    ]
  });
}
var DigitalSignatureValue_default = DigitalSignatureValue;

// src/components/Popover/index.tsx
import * as React3 from "react";
import { Popover } from "@mui/material";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var PopoverComponent = React3.forwardRef(
  (props, ref) => {
    const {
      children,
      Component: Component2,
      controlled
    } = props;
    const [anchorEl, setAnchorEl] = React3.useState(null);
    const popoverRef = React3.useRef(null);
    const controlledButtonRef = React3.useRef(null);
    const handleOpen = () => {
      if (controlledButtonRef.current) {
        setAnchorEl(controlledButtonRef.current);
      }
    };
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };
    React3.useImperativeHandle(ref, () => ({
      close: handleClose,
      open: handleOpen
    }));
    const open = Boolean(anchorEl);
    const id = open ? "popover" : void 0;
    const PoperComponent = React3.cloneElement(Component2, {
      close: () => handleClose()
    });
    return /* @__PURE__ */ jsxs3("div", {
      children: [
        /* @__PURE__ */ jsx4("div", {
          ref: controlledButtonRef,
          "aria-describedby": id,
          onClick: !controlled ? handleClick : void 0,
          children
        }),
        /* @__PURE__ */ jsx4(Popover, {
          ref: popoverRef,
          id,
          open,
          anchorEl,
          onClose: handleClose,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          },
          transformOrigin: {
            vertical: "center",
            horizontal: "center"
          },
          children: PoperComponent
        })
      ]
    });
  }
);
PopoverComponent.defaultProps = {
  controlled: false
};
PopoverComponent.propTypes = {};
var Popover_default = PopoverComponent;

// src/components/Confirmation/index.tsx
import { Button as Button2, Typography } from "@mui/material";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var Confirmation = function Confirmation2(props) {
  const {
    variant,
    text = "Are you sure?",
    close,
    confirm
  } = props;
  const getIcon = () => {
    switch (variant) {
      case "danger":
      case "warning":
      case "success":
      default:
        return void 0;
    }
  };
  return /* @__PURE__ */ jsxs4("div", {
    className: "sign-p-6",
    children: [
      getIcon(),
      /* @__PURE__ */ jsx5(Typography, {
        children: text
      }),
      /* @__PURE__ */ jsxs4("div", {
        className: "sign-flex sign-justify-center sign-pt-4 sign-gap-2",
        children: [
          /* @__PURE__ */ jsx5(Button2, {
            variant: "outlined",
            onClick: close,
            size: "small",
            children: "Cancel"
          }),
          /* @__PURE__ */ jsx5(Button2, {
            variant: "contained",
            onClick: confirm,
            size: "small",
            children: "Ok"
          })
        ]
      })
    ]
  });
};
Confirmation.defaultProps = {
  text: ""
};
Confirmation.propTypes = {};
var Confirmation_default = Confirmation;

// src/components/Upload/index.tsx
import { Button as Button3, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React4, { useId, useRef as useRef3, useState as useState4 } from "react";
import { centerCrop, makeAspectCrop, ReactCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Fragment as Fragment3, jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var CropImage = React4.forwardRef(
  (props, ref) => {
    const { src } = props;
    const [crop, setCrop] = useState4({
      unit: "px",
      x: 500,
      y: 200,
      width: 500,
      height: 200
    });
    const onImageLoad = (e) => {
      const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
      const cropDefault = centerCrop(
        makeAspectCrop(
          {
            unit: "%",
            width: 90
          },
          1,
          width,
          height
        ),
        width,
        height
      );
      setCrop(cropDefault);
    };
    const onCropComplete = () => new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const imageElement = document.getElementById("cropImage");
      if (!imageElement)
        return;
      const scaleX = imageElement.naturalWidth / imageElement.width;
      const scaleY = imageElement.naturalHeight / imageElement.height;
      canvas.width = crop.width || 0;
      canvas.height = crop.height || 0;
      const context = canvas.getContext("2d");
      if (!context)
        return;
      context.drawImage(
        imageElement,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], "croppedImage.png", {
            type: "image/png",
            lastModified: Date.now()
          });
          resolve(file);
        }
      }, "image/png");
    });
    React4.useImperativeHandle(ref, () => ({
      onCropComplete
    }));
    return /* @__PURE__ */ jsx6(ReactCrop, {
      keepSelection: true,
      crop,
      minWidth: 50,
      minHeight: 50,
      onChange: (c) => setCrop(c),
      children: /* @__PURE__ */ jsx6("img", {
        src,
        id: "cropImage",
        crossOrigin: "anonymous",
        alt: "edit",
        onLoad: onImageLoad,
        className: "max-h-[50vh]"
      })
    });
  }
);
function Upload({ onChange }) {
  const [open, setOpen] = useState4(null);
  const cropRef = useRef3(null);
  const onFileSelect = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileUrl = await toBase64(e.target.files[0]);
      setOpen(fileUrl);
    }
  };
  const onClickOk = async () => {
    if (cropRef.current) {
      const croppedFile = await cropRef.current.onCropComplete();
      const file = await toBase64(croppedFile);
      setOpen(null);
      if (onChange) {
        onChange(file);
      }
    }
  };
  const id = useId();
  const inputId = `sign-file_${id}`;
  return /* @__PURE__ */ jsxs5(Fragment3, {
    children: [
      /* @__PURE__ */ jsxs5("div", {
        children: [
          /* @__PURE__ */ jsx6("input", {
            className: "sign-hidden",
            id: inputId,
            onChange: onFileSelect,
            max: 1,
            type: "file",
            accept: ".png,.jpeg,.jpg"
          }),
          /* @__PURE__ */ jsxs5("label", {
            className: "sign-cursor-pointer sign-flex sign-items-center",
            htmlFor: inputId,
            children: [
              /* @__PURE__ */ jsxs5("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                width: "24",
                height: "24",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                "stroke-width": "2",
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                className: "sign-w-4 sign-h-4",
                children: [
                  /* @__PURE__ */ jsx6("path", {
                    d: "M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"
                  }),
                  /* @__PURE__ */ jsx6("path", {
                    d: "M14 2v4a2 2 0 0 0 2 2h4"
                  }),
                  /* @__PURE__ */ jsx6("path", {
                    d: "M12 12v6"
                  }),
                  /* @__PURE__ */ jsx6("path", {
                    d: "m15 15-3-3-3 3"
                  })
                ]
              }),
              /* @__PURE__ */ jsx6("span", {
                children: "Upload Signature"
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsxs5(Dialog, {
        open: Boolean(open),
        "aria-describedby": "alert-dialog-slide-description",
        transitionDuration: {
          enter: 500,
          exit: 500
        },
        onClose: () => setOpen(null),
        children: [
          /* @__PURE__ */ jsx6("div", {
            className: "absolute right-2 top-2",
            onClick: () => setOpen(null),
            children: /* @__PURE__ */ jsx6(Close_default, {
              className: "cursor-pointer"
            })
          }),
          /* @__PURE__ */ jsx6(DialogTitle, {
            sx: { textAlign: "center" },
            children: "Crop Image"
          }),
          /* @__PURE__ */ jsx6(DialogContent, {
            children: /* @__PURE__ */ jsx6(CropImage, {
              ref: cropRef,
              src: open
            })
          }),
          /* @__PURE__ */ jsxs5(DialogActions, {
            sx: {
              marginBottom: 2,
              justifyContent: "center"
            },
            children: [
              /* @__PURE__ */ jsx6(Button3, {
                type: "submit",
                onClick: () => setOpen(null),
                children: "Cancel"
              }),
              /* @__PURE__ */ jsx6(Button3, {
                color: "primary",
                variant: "contained",
                size: "large",
                onClick: onClickOk,
                children: "Ok"
              })
            ]
          })
        ]
      })
    ]
  });
}
var Upload_default = Upload;

// src/components/Signature/index.tsx
import { Fragment as Fragment4, jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var SignatureType = /* @__PURE__ */ ((SignatureType2) => {
  SignatureType2["digital"] = "digital";
  SignatureType2["draw"] = "draw";
  return SignatureType2;
})(SignatureType || {});
var Signature = class extends Component {
  constructor(props) {
    var _a, _b;
    super(props);
    this.getMetaInfo = async () => {
      return new Promise(async (resolve, reject) => {
        const data = sessionStorage.getItem("BROWSER_DATA");
        if (data) {
          const metaData = JSON.parse(data);
          this.setState({
            meta: {
              ...metaData,
              ipAddress: metaData.ip,
              browser: parseUserAgent(navigator.userAgent),
              time: new Date().toUTCString()
            },
            metaLoading: false
          }, () => {
            resolve({
              ...metaData,
              ipAddress: metaData.ip,
              browser: parseUserAgent(navigator.userAgent),
              time: new Date().toUTCString()
            });
          });
        }
        this.setState({
          metaLoading: true
        }, async () => {
          const response = await fetch("https://api64.ipify.org?format=json");
          const { ip } = await response.json();
          sessionStorage.setItem(
            "BROWSER_DATA",
            JSON.stringify({
              ipAddress: ip,
              browser: parseUserAgent(navigator.userAgent)
            })
          );
          const { meta } = this.state;
          this.setState({
            meta: {
              ...meta,
              ipAddress: ip,
              browser: parseUserAgent(navigator.userAgent),
              time: new Date().toUTCString()
            },
            metaLoading: false
          }, () => {
            resolve({
              ...meta,
              ipAddress: ip,
              browser: parseUserAgent(navigator.userAgent),
              time: new Date().toUTCString()
            });
          });
        });
      });
    };
    this.lazyLoadFonts = () => {
      const linkTags = Object.keys(fontFamilies_default).map((font) => {
        const importFamily = fontFamilies_default[font].import;
        return `<link rel="stylesheet" href="https://fonts.googleapis.com/css?family=${importFamily}" as="style" crossorigin="anonymous" onload="this.onload=null;this.rel='stylesheet'" />`;
      });
      const head = document.head || document.getElementsByTagName("head")[0];
      linkTags.forEach((linkTag) => {
        const div = document.createElement("div");
        div.innerHTML = linkTag;
        head.appendChild(div.firstChild);
      });
    };
    this.handleClose = () => {
      this.setState({ open: false, localValue: void 0 });
    };
    this.handleSelectSignature = () => {
      this.clearSignature();
      this.setState({ signatureType: "digital" /* digital */ });
    };
    this.clearSignature = () => {
      if (this.signpadRef.current) {
        this.signpadRef.current.clear();
      }
      this.setState({
        localValue: void 0,
        signatureType: "draw" /* draw */
      }, () => {
        var _a, _b;
        this.props.onChange && this.props.onChange(null);
        ((_a = this.clearRef.current) == null ? void 0 : _a.close) && ((_b = this.clearRef.current) == null ? void 0 : _b.close());
      });
    };
    this.onDrawEnd = async () => {
      if (this.signpadRef.current.isEmpty())
        return;
      const signData = this.signpadRef.current.toDataURL();
      const { meta } = this.state;
      let value = {
        type: "draw" /* draw */,
        data: {
          image: signData
        },
        meta: {
          ...meta,
          time: new Date().toISOString()
        }
      };
      if (!meta) {
        const latestMeta = await this.getMetaInfo();
        value = {
          type: "draw" /* draw */,
          data: {
            image: signData
          },
          meta: {
            ...latestMeta,
            time: new Date().toISOString()
          }
        };
      }
      this.setState({
        localValue: value
      }, () => {
        this.submitSign();
      });
    };
    this.handleGoback = () => {
      this.setState({ signatureType: "draw" /* draw */ });
    };
    this.onSelectFont = async (fontFamily) => {
      const { user, meta } = this.state;
      if (user) {
        let value = {
          type: "digital" /* digital */,
          data: {
            fontFamily,
            name: user == null ? void 0 : user.name
          },
          meta: {
            ...meta,
            time: new Date().toISOString()
          }
        };
        if (!meta) {
          const latestMeta = await this.getMetaInfo();
          value = {
            type: "digital" /* digital */,
            data: {
              fontFamily,
              name: user == null ? void 0 : user.name
            },
            meta: {
              ...latestMeta,
              time: new Date().toISOString()
            }
          };
        }
        this.setState({
          localValue: value
        });
      }
    };
    this.submitSign = () => {
      const { localValue } = this.state;
      this.props.onChange && this.props.onChange(localValue);
      this.setState({
        open: false,
        localValue: void 0,
        value: localValue
      });
    };
    this.submitUploadSign = async (signData) => {
      const { meta } = this.state;
      let value = {
        type: "draw" /* draw */,
        data: {
          image: signData
        },
        meta: {
          ...meta,
          time: new Date().toISOString()
        }
      };
      if (!meta) {
        const latestMeta = await this.getMetaInfo();
        value = {
          type: "draw" /* draw */,
          data: {
            image: signData
          },
          meta: {
            ...latestMeta,
            time: new Date().toISOString()
          }
        };
      }
      this.setState({
        localValue: value
      });
    };
    this.handleOnClick = () => {
      const { readonly } = this.props;
      if (readonly) {
        return;
      }
      this.setState({ open: true });
    };
    this.getProps = () => {
      const { signatureType, user, value, localValue } = this.state;
      const { styles, readonly, renderButton, renderFooter, disableUploadSignature } = this.props;
      return {
        signatureType,
        user,
        value,
        localValue,
        styles,
        readonly,
        renderButton,
        renderFooter,
        disableUploadSignature
      };
    };
    this.state = {
      open: props.open && !props.readonly || false,
      signatureType: ((_a = props == null ? void 0 : props.user) == null ? void 0 : _a.name) && ((_b = props == null ? void 0 : props.user) == null ? void 0 : _b.name.trim().length) > 0 && props.initType === "digital" /* digital */ ? "digital" /* digital */ : "draw" /* draw */,
      value: props.value,
      localValue: props.value,
      user: props.user,
      metaLoading: false
    };
    this.signpadRef = React5.createRef();
    this.clearRef = React5.createRef();
  }
  async componentDidMount() {
    this.lazyLoadFonts();
  }
  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ localValue: this.props.value, value: this.props.value });
    }
    if (prevProps.user !== this.props.user) {
      this.setState({ user: this.props.user });
    }
  }
  render() {
    const {
      signatureType,
      user,
      value,
      localValue,
      styles,
      readonly,
      renderButton,
      renderFooter,
      disableUploadSignature
    } = this.getProps();
    return /* @__PURE__ */ jsxs6(Fragment4, {
      children: [
        !value && /* @__PURE__ */ jsx7("div", {
          className: readonly ? "sign-cursor-not-allowed" : "sign-cursor-pointer",
          style: { height: "100%", ...styles == null ? void 0 : styles.root },
          onClick: this.handleOnClick,
          children: renderButton ? renderButton() : /* @__PURE__ */ jsx7("div", {
            className: readonly ? "sign-cursor-not-allowed" : "sign-cursor-pointer",
            children: "Click here to sign"
          })
        }),
        value && /* @__PURE__ */ jsx7(DigitalSignatureValue_default, {
          styles: this.props.styles,
          value,
          readonly,
          onClick: this.handleOnClick
        }),
        this.state.open && /* @__PURE__ */ jsxs6(Dialog2, {
          open: this.state.open,
          onClose: this.handleClose,
          maxWidth: "sm",
          fullWidth: true,
          children: [
            /* @__PURE__ */ jsx7(DialogTitle2, {
              sx: { m: 0, p: 2 },
              id: "customized-dialog-title",
              children: "Signature"
            }),
            /* @__PURE__ */ jsx7(IconButton, {
              "aria-label": "close",
              onClick: this.handleClose,
              sx: {
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500]
              },
              children: /* @__PURE__ */ jsx7(Close_default, {})
            }),
            /* @__PURE__ */ jsxs6(DialogContent2, {
              className: "sign-flex sign-flex-col sign-gap-1 sign-mx-auto sign-justify-between sign-items-center",
              children: [
                !localValue && signatureType === "draw" /* draw */ && /* @__PURE__ */ jsxs6(Fragment4, {
                  children: [
                    /* @__PURE__ */ jsx7(ReactSignatureCanvas.default, {
                      ref: this.signpadRef,
                      canvasProps: { width: 500, height: 200, className: "sign-border-solid" }
                    }),
                    /* @__PURE__ */ jsxs6("div", {
                      className: "sign-flex  sign-items-start sign-w-full sign-flex-between",
                      children: [
                        user && (user == null ? void 0 : user.name.length) > 0 && /* @__PURE__ */ jsxs6("div", {
                          className: "sign-flex sign-flex-col sign-items-start sign-w-full",
                          children: [
                            /* @__PURE__ */ jsx7("span", {
                              className: "sign-w-full sign-text-sm",
                              children: `${user == null ? void 0 : user.name}`
                            }),
                            /* @__PURE__ */ jsx7("div", {
                              onClick: this.handleSelectSignature,
                              className: "sign-text-sm sign-underline sign-cursor-pointer text-xs",
                              children: "Digital signature"
                            })
                          ]
                        }),
                        /* @__PURE__ */ jsx7("div", {
                          className: "sign-text-sm sign-flex sign-underline sign-justify-end sign-w-full",
                          children: !disableUploadSignature && /* @__PURE__ */ jsx7(Upload_default, {
                            onChange: this.submitUploadSign
                          })
                        })
                      ]
                    }),
                    /* @__PURE__ */ jsxs6("div", {
                      className: "sign-flex sign-gap-4 sign-mt-6",
                      children: [
                        value && /* @__PURE__ */ jsx7(Popover_default, {
                          ref: this.clearRef,
                          Component: /* @__PURE__ */ jsx7(Confirmation_default, {
                            variant: "warning",
                            text: "Are you sure you want to clear?",
                            confirm: this.clearSignature
                          }),
                          children: /* @__PURE__ */ jsx7(Button4, {
                            type: "submit",
                            children: "Clear"
                          })
                        }),
                        !value && /* @__PURE__ */ jsx7(Button4, {
                          type: "submit",
                          onClick: () => this.clearSignature(),
                          children: "Clear"
                        }),
                        /* @__PURE__ */ jsx7(Button4, {
                          variant: "contained",
                          onClick: this.onDrawEnd,
                          autoFocus: true,
                          children: "Submit"
                        })
                      ]
                    })
                  ]
                }),
                !localValue && signatureType === "digital" /* digital */ && /* @__PURE__ */ jsx7(DigitalSignaturePicker_default, {
                  onSelectFont: this.onSelectFont,
                  onClickGoback: this.handleGoback,
                  user
                }),
                localValue && /* @__PURE__ */ jsxs6(Fragment4, {
                  children: [
                    /* @__PURE__ */ jsx7("div", {
                      className: "sign-flex sign-justify-center sign-items-center sign-py-6 sign-px-6 sign-border-solid",
                      children: /* @__PURE__ */ jsx7("div", {
                        className: "sign-flex sign-justify-center sign-items-center sign-w-[500px] sign-h-[100px]",
                        children: /* @__PURE__ */ jsx7(DigitalSignatureValue_default, {
                          preview: true,
                          value: localValue
                        })
                      })
                    }),
                    /* @__PURE__ */ jsxs6("div", {
                      className: "sign-flex sign-gap-4 mt-6 sign-mt-6",
                      children: [
                        value && /* @__PURE__ */ jsx7(Popover_default, {
                          ref: this.clearRef,
                          Component: /* @__PURE__ */ jsx7(Confirmation_default, {
                            variant: "warning",
                            text: "Are you sure you want to clear?",
                            confirm: this.clearSignature
                          }),
                          children: /* @__PURE__ */ jsx7(Button4, {
                            type: "submit",
                            children: "Clear"
                          })
                        }),
                        !value && /* @__PURE__ */ jsx7(Button4, {
                          type: "submit",
                          onClick: () => this.clearSignature(),
                          children: "Clear"
                        }),
                        /* @__PURE__ */ jsx7(Button4, {
                          variant: "contained",
                          disabled: !localValue,
                          onClick: this.submitSign,
                          autoFocus: true,
                          children: "Submit"
                        })
                      ]
                    })
                  ]
                })
              ]
            }),
            renderFooter && renderFooter()
          ]
        })
      ]
    });
  }
};
export {
  Signature,
  SignatureType
};
